import { useEffect, type FC } from 'react';

import {
  Button,
  Heading,
  ModalBase,
  ModalCloseButton,
  ModalPortal,
  Text,
} from '@livechat/design-system-react-components';

import { StackingContextLevel } from 'styles/stacking-context-level';

import { getLegacyMigrationModalTitle, LEGACY_MIGRATION_MODAL_COPY, LegacyMigrationEvent } from './constants';
import { trackLegacyMigrationEvent } from './helpers';
import { useMigrationDaysLeft } from './use-migration-days-left';

import * as styles from './styles';

interface Props {
  imageSource: string;
  onClose(): void;
  onPrimaryAction(): void;
}

export const LegacyMigrationModal: FC<Props> = ({ imageSource, onClose, onPrimaryAction }) => {
  const daysLeft = useMigrationDaysLeft();

  useEffect(() => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalDisplayed, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'CTA text': LEGACY_MIGRATION_MODAL_COPY.primaryAction,
    });
  }, []);

  const handleClose = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalDismissed);
    onClose();
  };

  const handlePrimaryAction = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.ModalPrimaryActionClicked);
    onPrimaryAction();
  };

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ModalBase className={styles.modal} fullSpaceContent onClose={handleClose} closeOnEscPress>
        <div className={styles.closeButton}>
          <ModalCloseButton onClick={handleClose} />
        </div>
        <img className={styles.media} src={imageSource} alt="Text App" width={592} height={240} />
        <div className={styles.content}>
          <Heading as="h2" size="lg" className={styles.title}>
            {getLegacyMigrationModalTitle(daysLeft)}
          </Heading>
          <Text size="md" className={styles.description}>
            {LEGACY_MIGRATION_MODAL_COPY.descriptionLine1}
          </Text>
          <Text size="md" className={styles.description}>
            {LEGACY_MIGRATION_MODAL_COPY.descriptionLine2}
          </Text>
          <div className={styles.actions}>
            <Button size="large" kind="high-contrast" onClick={handlePrimaryAction}>
              {LEGACY_MIGRATION_MODAL_COPY.primaryAction}
            </Button>
          </div>
        </div>
      </ModalBase>
    </ModalPortal>
  );
};
